/**
 * useAudio.js
 * React hook for real-time microphone capture and formant frequency estimation.
 *
 * Uses Web Audio API's AnalyserNode (FFT) + spectral peak detection
 * to estimate F1 and F2 formant frequencies from live microphone input.
 * No external audio library required.
 *
 * Based on MouthShapeCoach.jsx reference code.
 */
import { useEffect, useRef, useState, useCallback } from 'react';

// ── Formant Estimation (spectral peak detection) ──────────────────────────────

/**
 * Smoothed formant state — persists across frames for EMA smoothing.
 * Kept as a module-level variable so it survives re-renders.
 */
const formantState = { f1: 0, f2: 0, f3: 0 };

/**
 * Find the strongest spectral peak within a Hz range.
 * @param {Float32Array} smoothed – smoothed frequency magnitude array
 * @param {number} loHz – lower bound in Hz
 * @param {number} hiHz – upper bound in Hz
 * @param {number} binHz – Hz per FFT bin
 * @returns {{ hz: number, strength: number }}
 */
function peakInRange(smoothed, loHz, hiHz, binHz) {
  const N = smoothed.length;
  const lo = Math.max(2, Math.floor(loHz / binHz));
  const hi = Math.min(N - 2, Math.ceil(hiHz / binHz));
  let best = { hz: 0, strength: 0 };

  for (let i = lo; i <= hi; i++) {
    // Local peak: stronger than both neighbors AND above noise floor
    if (smoothed[i] > smoothed[i - 1] && smoothed[i] > smoothed[i + 1]
        && smoothed[i] > best.strength && smoothed[i] > 22) {
      best = { hz: i * binHz, strength: smoothed[i] };
    }
  }
  return best;
}

/**
 * Estimate formant frequencies from FFT frequency data.
 * @param {Uint8Array} freqData – from analyser.getByteFrequencyData()
 * @param {number} binHz – Hz per FFT bin (sampleRate / fftSize)
 * @returns {{ f1: number, f2: number, f3: number, energy: number, highBandRatio: number }}
 */
function analyzeSoundFrame(freqData, binHz) {
  const N = freqData.length;

  // Total + high-band energy
  let totalMag = 0;
  let highBandMag = 0;
  for (let i = 0; i < N; i++) {
    const hz = i * binHz;
    totalMag += freqData[i];
    if (hz >= 3500 && hz <= 8000) highBandMag += freqData[i];
  }
  const energy = totalMag / N;

  // Only estimate formants if energy is above noise floor
  if (energy > 14) {
    // 5-point moving average smoothing
    const s = new Float32Array(N);
    for (let i = 2; i < N - 2; i++) {
      s[i] = (freqData[i - 2] + freqData[i - 1] + freqData[i]
            + freqData[i + 1] + freqData[i + 2]) / 5;
    }

    const f1 = peakInRange(s, 250, 1000, binHz);   // Vowel height
    const f2 = peakInRange(s, 900, 3000, binHz);    // Vowel frontness
    const f3 = peakInRange(s, 2200, 4500, binHz);   // Timbre

    // Exponential moving average (alpha = 0.14)
    const alpha = 0.14;
    if (f1.hz > 0) formantState.f1 = alpha * f1.hz + (1 - alpha) * formantState.f1;
    if (f2.hz > 0) formantState.f2 = alpha * f2.hz + (1 - alpha) * formantState.f2;
    if (f3.hz > 0) formantState.f3 = alpha * f3.hz + (1 - alpha) * formantState.f3;
  }

  return {
    f1: formantState.f1,
    f2: formantState.f2,
    f3: formantState.f3,
    energy,
    highBandRatio: totalMag ? highBandMag / totalMag : 0,
  };
}

// ── Sound Scoring ────────────────────────────────────────────────────────────

const F1_TOLERANCE = 160; // Hz
const F2_TOLERANCE = 380; // Hz

/**
 * Score how well detected formants match the exercise target.
 * @param {object} exercise – exercise object with target_f1_min/max, target_f2_min/max, target_shape_type
 * @param {object|null} soundMetrics – output of analyzeSoundFrame()
 * @returns {{ status: 'waiting'|'try'|'good', label: string }}
 */
export function scoreSound(exercise, soundMetrics) {
  if (!soundMetrics || soundMetrics.energy < 14) {
    return { status: 'waiting', label: 'Waiting for sound…' };
  }

  const hasFormant = exercise.target_f1_min != null && exercise.target_f2_min != null;
  const shape = exercise.target_shape_type;

  // ── Shapes with F1/F2 ranges (vowels + consonants with vowel component) ──
  if (hasFormant) {
    const f1ok = soundMetrics.f1 >= exercise.target_f1_min
              && soundMetrics.f1 <= exercise.target_f1_max;
    const f2ok = soundMetrics.f2 >= exercise.target_f2_min
              && soundMetrics.f2 <= exercise.target_f2_max;

    if (f1ok && f2ok) {
      return { status: 'good', label: '✅ Sound matches!' };
    }
    return { status: 'try', label: 'Sound heard — keep adjusting' };
  }

  // ── Shapes without F1/F2: camera-only, sound not required ──
  return { status: 'good', label: 'Camera only for this shape' };
}

// ── React Hook ───────────────────────────────────────────────────────────────

/**
 * Custom hook for microphone capture + real-time formant analysis.
 * Does NOT set React state every frame — exposes a getSoundMetrics()
 * function to read the latest audio synchronously (avoids re-render flood).
 *
 * @param {boolean} isActive – start/stop mic capture
 * @returns {{ isReady: boolean, getSoundMetrics: () => object|null }}
 */
export default function useAudio(isActive) {
  const analyserRef  = useRef(null);
  const freqDataRef  = useRef(null);
  const binHzRef     = useRef(0);
  const streamRef    = useRef(null);
  const audioCtxRef  = useRef(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!isActive) return;

    let cancelled = false;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }

        const audioCtx = new AudioContext();
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 2048;
        audioCtx.createMediaStreamSource(stream).connect(analyser);

        streamRef.current   = stream;
        audioCtxRef.current  = audioCtx;
        analyserRef.current  = analyser;
        freqDataRef.current  = new Uint8Array(analyser.frequencyBinCount);
        binHzRef.current     = audioCtx.sampleRate / analyser.fftSize;

        setIsReady(true);
      } catch (err) {
        console.error('Microphone setup failed:', err);
      }
    })();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
      analyserRef.current = null;
      freqDataRef.current = null;
      // Reset formant EMA state
      formantState.f1 = 0;
      formantState.f2 = 0;
      formantState.f3 = 0;
      setIsReady(false);
    };
  }, [isActive]);

  /**
   * Read current audio data synchronously — call this inside
   * the landmarks useEffect to get sound metrics for the same "frame".
   */
  const getSoundMetrics = useCallback(() => {
    if (!analyserRef.current || !freqDataRef.current) return null;
    analyserRef.current.getByteFrequencyData(freqDataRef.current);
    return analyzeSoundFrame(freqDataRef.current, binHzRef.current);
  }, []);

  return { isReady, getSoundMetrics };
}
